import React, { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { ActionRefresh, Button, Table, ActionEdit } from '@demandlocal/components'
import { ReactComponent as ActionDelete } from '../../../images/icons/action_delete.svg'
import AgencyReportExportForm from '../../../forms/Agency/AgencyReportExport'
import { createColumnHelper } from '@tanstack/react-table'
import { useGetAgencyExports } from '../../../api/agencies/reports/useGetAgencyExports'
import { getDayOrdinal } from '../../../lib/utils/getDayOrdinal'
import { useDeleteAgencyExport } from '../../../api/agencies/reports/useDeleteAgencyExport'
import toast from 'react-hot-toast'
import { useRunExportRefresh } from '../../../api/agencies/reports/useRunExportRefresh'
import dayjs from 'dayjs'
import classNames from 'classnames'

const columnHelper = createColumnHelper<{
  _id: string
  recurring: boolean
  recurring_interval: {
    type: string
    day?: number
  }
  createdAt: Date
  updatedAt: Date
  download_url: string
  next_run_at: Date
  name: string
  status: string
  email: string
  reportFormat: string
}>()
export default function AgencyExports(): JSX.Element {
  const { agency_id } = useParams()
  const [expanded, setExpanded] = useState(false)
  const [exportId, setExportId] = useState<string | undefined>()
  const { mutateAsync: deleteExport } = useDeleteAgencyExport()

  const { mutate: fetchExport } = useRunExportRefresh()

  const triggerExportRefresh = useCallback(
    (id: string) => {
      const toastId = toast.loading('Refreshing export...')
      fetchExport(
        { agency_id: Number(agency_id), data: { id } },
        {
          onSuccess: () => {
            toast.success('Source is pulling data in the background.', { id: toastId })
          },
        },
      )
    },
    [agency_id, fetchExport],
  )

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => (
          <a
            className="font-bold text-primary-persian-blue"
            target="_blank"
            rel="noreferrer"
            href={info.row.original.download_url}>
            {info.getValue()}
          </a>
        ),
      }),
      columnHelper.accessor('email', {
        header: 'Email to',
        cell: (info) => info.getValue(),
      }),
      columnHelper.display({
        id: 'frequency',
        header: 'Frequency',
        cell: ({ row }) => {
          if (!row.original.recurring) return 'One Time'
          if (row.original.recurring_interval.type === 'daily') return 'Daily'

          const day = row?.original?.recurring_interval?.day
          if (row.original.recurring_interval.type === 'monthly')
            return `Every ${day}${getDayOrdinal(Number(day))} of the month`
        },
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created Date',
        cell: (info) => dayjs(info.getValue()).format('MMM D, YYYY'),
      }),
      columnHelper.accessor('updatedAt', {
        header: 'Last Run',
        cell: (info) => dayjs(info.getValue()).format('MMM D, YYYY - HH:mm'),
      }),
      columnHelper.accessor('reportFormat', {
        header: 'Type',
        cell: (info) => {
          const type = info.getValue()
          if (type === 'xlsx') return 'Excel'
          if (type === 'csv') return 'CSV'
          if (type === 'google_sheet') return 'Google Sheet'
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const disabled = row.original.status === 'locked'
          return (
            <div className="flex flex-row gap-2 items-center">
              <ActionEdit
                // @ts-ignore
                className={`cursor-pointer stroke-primary-persian-blue`}
                onClick={() => {
                  setExportId(row.original._id)
                  setExpanded(true)
                }}
              />
              <ActionRefresh
                // @ts-ignore
                className={`cursor-pointer  ${
                  !disabled
                    ? 'stroke-primary-persian-blue  fill-primary-persian-blue'
                    : 'stroke-web-gray  fill-web-gray'
                }`}
                onClick={() => {
                  if (!disabled) triggerExportRefresh(row.original._id)
                }}
              />
              <ActionDelete
                // @ts-ignore
                className={'cursor-pointer'}
                onClick={async () => {
                  if (window.confirm('Are you sure you want to delete this export?')) {
                    await deleteExport({
                      agency_id: Number(agency_id),
                      id: row.original._id,
                    })
                  }
                }}
              />
            </div>
          )
        },
      }),
    ],
    [agency_id, deleteExport, triggerExportRefresh],
  )

  return (
    <div className={'flex flex-1 flex-col pb-8'}>
      <div className={'pb-4 flex justify-between'}>
        <div className={'flex flex-row justify-between items-center w-full'}>
          <div className={'font-bold text-base text-primary-xiketic'}>Exports</div>
          <div className="flex flex-row gap-2">
            <Button
              animate={true}
              type={'button'}
              variant={'tertiary'}
              customStyles={classNames('print:hidden', {
                '!bg-primary-persian-blue/5 !text-primary-persian-blue !border-primary-persian-blue/10':
                  expanded,
              })}
              onClick={(e) => {
                e.preventDefault()
                setExportId(undefined)
                setExpanded((prev) => !prev)
              }}>
              + Create a new agency export
            </Button>
          </div>
        </div>
      </div>
      <div className={'flex flex-1 flex-col gap-2 relative overflow-hidden'}>
        {expanded && (
          <AgencyReportExportForm export_id={exportId} setExpanded={setExpanded} />
        )}
        <Table
          columns={columns}
          fetchFn={useGetAgencyExports}
          filters={{ agency_id: Number(agency_id) }}
        />
      </div>
    </div>
  )
}
